import { AddJavascriptToHeader } from '@/services/AoeHelpers.ts'
import { GetCultureName, GetOrderEntryConnectorUrl } from '@/stores/SettingsStore'
import type { iCheckoutPaymentViewModel } from '@/components/AoeCheckoutPayment.vue'
export const BrainTreeControlFolderName = 'TTGBraintree'
export const BrainTreeAPMControlFolderName = 'TTGBraintreeAPM'
import { CartPaymentTokenCreate } from '@/api/Cart'
import type { IAoeCart } from '@/types/CartTypes'
import { useStoreLogger } from '@/logger.ts'
import { handleError } from '@/services/ErrorService.ts'
import type { ComposerTranslation } from 'vue-i18n'
import { aoeBraintreePaypalCheckout } from '@/components/PaymentGateways/Braintree/AoeBraintreePaypalCheckout.ts'
import { aoeBraintreePaypalVault } from '@/components/PaymentGateways/Braintree/AoeBraintreePaypalVault.ts'
import { aoeBraintreeAPM } from '@/components/PaymentGateways/Braintree/AoeBraintreeAPM.ts'
import { getFunnelType } from '@/analytics/index'

export async function SetupBraintree(
  t: ComposerTranslation,
  cart: IAoeCart,
  viewModels: iCheckoutPaymentViewModel[],
) {
  const logger = useStoreLogger()
  const braintreePaymentMethod = viewModels.find(
    vm => vm.controlFolderName === BrainTreeControlFolderName,
  )
  if (braintreePaymentMethod == null) {
    logger.error('SetupTtgBraintree', 'braintreePaymentMethod not found')
    return
  }
  const paypalMerchantId = braintreePaymentMethod.parameters?.PaypalMerchantId
  if (paypalMerchantId) {
    await CartPaymentTokenCreate({
      aoeCartIdToken: cart.aoeCartIdToken,
      systemKeyword: braintreePaymentMethod.systemKeyword,
      charge: cart.balance,
    })
      .then(async response => {
        if (response.item.ClientToken) {
          let payPalFlow = braintreePaymentMethod.parameters?.PayPalFlow
          if (!payPalFlow) payPalFlow = 'VAULT'
          const locale = (await GetCultureName()).replace('-', '_')
          logger.debug(
            'SetupTtgBraintree',
            'paypalMerchantId:',
            paypalMerchantId,
            'locale:',
            locale,
            'payPalFlow:',
            payPalFlow,
          )
          const funnelType = getFunnelType(cart.orderTypeId, cart.eventId)
          if (payPalFlow === 'VAULT') {
            aoeBraintreePaypalVault.paypalScriptUrl =  `https://www.paypal.com/sdk/js?client-id=${paypalMerchantId}`
            aoeBraintreePaypalVault.braintreeScriptUrl = 'https://js.braintreegateway.com/web/3.129.0/js/paypal-checkout.min.js'
            aoeBraintreePaypalVault.clientToken = response.item.ClientToken
            aoeBraintreePaypalVault.paymentValues.paymentAmount = cart.balance.toString()
            aoeBraintreePaypalVault.createPaymentUrl = await GetOrderEntryConnectorUrl('CartPaymentCreate')
            aoeBraintreePaypalVault.sourceValues.aOECartIdToken = cart.aoeCartIdToken
            aoeBraintreePaypalVault.sourceValues.systemKeyword = braintreePaymentMethod.systemKeyword
            aoeBraintreePaypalVault.cartCurrency = cart.currencyId || 'EUR'
            aoeBraintreePaypalVault.cartFunnelType = funnelType
            aoeBraintreePaypalVault.Initialize()
          }
          else {
            aoeBraintreePaypalCheckout.paypalScriptUrl =  `https://www.paypal.com/sdk/js?client-id=${paypalMerchantId}`
            aoeBraintreePaypalCheckout.braintreeScriptUrl = 'https://js.braintreegateway.com/web/3.129.0/js/paypal-checkout.min.js'
            aoeBraintreePaypalCheckout.clientToken = response.item.ClientToken
            aoeBraintreePaypalCheckout.paymentValues.paymentAmount = cart.balance.toString()
            aoeBraintreePaypalCheckout.paymentValues.currencyCode = cart.currencyId
            aoeBraintreePaypalCheckout.createPaymentUrl = await GetOrderEntryConnectorUrl('CartPaymentCreate')
            aoeBraintreePaypalCheckout.sourceValues.aOECartIdToken = cart.aoeCartIdToken
            aoeBraintreePaypalCheckout.sourceValues.systemKeyword = braintreePaymentMethod.systemKeyword
            aoeBraintreePaypalCheckout.cartFunnelType = funnelType
            aoeBraintreePaypalCheckout.Initialize()
          }
        } else {
          logger.error('SetupTtgBraintree', 'response:', response)
        }
      })
      .catch(async error => {
        await handleError(error, t)
      })
  }
  AddJavascriptToHeader(
    'https://js.braintreegateway.com/web/3.129.0/js/client.min.js',
  )
  AddJavascriptToHeader(
    'https://js.braintreegateway.com/web/3.129.0/js/three-d-secure.min.js',
  )
  AddJavascriptToHeader(
    'https://js.braintreegateway.com/web/3.129.0/js/apple-pay.min.js',
  )
  AddJavascriptToHeader(
    'https://js.braintreegateway.com/web/dropin/1.45.2/js/dropin.min.js',
  )
  AddJavascriptToHeader(
    'https://js.braintreegateway.com/web/3.129.0/js/hosted-fields.js',
  )
  AddJavascriptToHeader(
    'https://www.paypal.com/sdk/js?client-id=AdcdhkfMdglSOykb08jG4z7ezyILtA0YKgiuvHmZOLt8Ae43kMckwN_CgsmLMYE5xOPWxid6YG_JMMZa',
  )
  AddJavascriptToHeader(
    'https://js.braintreegateway.com/web/3.129.0/js/paypal-checkout.min.js',
  )
}
export async function SetupBraintreeAPM(
  t: ComposerTranslation,
  cart: IAoeCart,
  viewModels: iCheckoutPaymentViewModel[],
) {
  const logger = useStoreLogger()
  const apmPaymentMethod = viewModels.find(
    vm => vm.controlFolderName === BrainTreeAPMControlFolderName,
  )
  if (apmPaymentMethod == null) {
    logger.error('SetupBraintreeAPM', 'apmPaymentMethod not found')
    return
  }

  try {
    // Load necessary scripts for APM
    AddJavascriptToHeader('https://js.braintreegateway.com/web/3.129.0/js/client.min.js')
    AddJavascriptToHeader('https://js.braintreegateway.com/web/3.129.0/js/local-payment.min.js')

    // Get client token
    const response = await CartPaymentTokenCreate({
      aoeCartIdToken: cart.aoeCartIdToken,
      systemKeyword: apmPaymentMethod.systemKeyword,
      charge: cart.balance,
    })

    if (response.item.ClientToken) {
      logger.debug('SetupBraintreeAPM', 'Successfully obtained client token')

      // Configure APM component
      aoeBraintreeAPM.clientToken = response.item.ClientToken
      aoeBraintreeAPM.paymentValues.paymentAmount = cart.balance.toString()
      aoeBraintreeAPM.paymentValues.currencyCode = cart.currencyId
      aoeBraintreeAPM.createPaymentUrl = await GetOrderEntryConnectorUrl('CartPaymentCreate')
      aoeBraintreeAPM.sourceValues.aOECartIdToken = cart.aoeCartIdToken
      aoeBraintreeAPM.sourceValues.systemKeyword = apmPaymentMethod.systemKeyword

      // Add merchant account ID if available in the payment method
      if (apmPaymentMethod.parameters?.merchantAccountId) {
        aoeBraintreeAPM.merchantAccountId = apmPaymentMethod.parameters.merchantAccountId
      } else {
        aoeBraintreeAPM.merchantAccountId = 'tpcaustriaEUR' // Default merchant account ID for EPS
      }

      // Set customer email for payment
      if (cart.customerEmailAddress) {
        aoeBraintreeAPM.customerEmail = cart.customerEmailAddress
      } else if (cart.guestEmailAddress) {
        aoeBraintreeAPM.customerEmail = cart.guestEmailAddress
      }

      if (cart.customerFirstName || cart.customerLastName) {
        aoeBraintreeAPM.customerFirstName = cart.customerFirstName
        aoeBraintreeAPM.customerLastName = cart.customerLastName
      } else {
        aoeBraintreeAPM.customerFirstName = cart.guestFirstName
        aoeBraintreeAPM.customerLastName = cart.guestLastName
      }

      // Don't Initialize APM component - it will be initialized when the component is mounted
      //aoeBraintreeAPM.Initialize()
    } else {
      logger.error('SetupBraintreeAPM', 'Failed to obtain client token', response)
    }
  } catch (error) {
    logger.error('SetupBraintreeAPM', 'Error setting up Braintree APM', error)
    await handleError(error, t)
  }
}
